import type { Callout, Job, ServiceStatus } from '@/lib/schemas/db'
import { isServiceLocked } from '@/lib/serviceLock'

import type {
  Log,
  NewCalloutInput,
  NewLogInput,
  NewStatusChangeInput,
  StatusChangeLog,
  UpdateCalloutInput,
  UpdateLogInput,
} from '../domain/models'
import {
  assertCalloutStatusChangeAllowed,
} from '../domain/calloutStatus'
import {
  statusRecordsAnEventTime,
  statusRequiresReason,
} from '../domain/statusRules'
import type {
  AuthStorePort,
  CalloutsRepositoryPort,
  ClockPort,
  IdGeneratorPort,
  NotesRepositoryPort,
  StatusChangesRepositoryPort,
  JobsRepositoryPort,
} from '../ports'

export function createCallout(
  input: NewCalloutInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    calloutsRepo: CalloutsRepositoryPort
  },
): {
  callout: Callout
  transaction: ReturnType<CalloutsRepositoryPort['insert']>
} {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  if (input.status === 'New') {
    throw new Error('New Callouts must be created with a concrete status (for example Booked).')
  }

  const callout: Callout = {
    id: deps.idGenerator.generateId(),
    createdAt: timestamp,
    updatedAt: timestamp,
    ...input,
    authorId: currentUser.id,
  }
  const transaction = deps.calloutsRepo.insert(callout)
  return { callout, transaction }
}

export function updateCallout(
  id: string,
  patch: UpdateCalloutInput,
  deps: {
    clock: ClockPort
    calloutsRepo: CalloutsRepositoryPort
    /**
     * Transition guard for the service type being written. Sessions share this
     * command but have their own statuses (Started/Ended, not Arrived/LeftSite),
     * so they must pass their own assertion — the callout map rejects every
     * session move (it has no Started/Ended), which left sessions stuck.
     */
    assertStatusChangeAllowed?: (
      currentStatus: Callout['status'],
      nextStatus: Callout['status'],
    ) => void
  },
) {
  const timestamp = deps.clock.now()
  const assertAllowed =
    deps.assertStatusChangeAllowed ?? assertCalloutStatusChangeAllowed
  return deps.calloutsRepo.update(id, (draft) => {
    const {
      statusReason: _statusReason,
      statusOccurredAt: _statusOccurredAt,
      ...persistedPatch
    } = patch
    const currentStatus = draft.status
    const requestedStatus = persistedPatch.status ?? currentStatus
    const hasNonStatusChanges = Object.keys(persistedPatch).some(
      (key) => key !== 'status',
    )

    if (currentStatus === 'Invoiced') {
      if (requestedStatus !== currentStatus) {
        assertAllowed(currentStatus, requestedStatus)
      }

      if (hasNonStatusChanges) {
        throw new Error(
          'Invoiced Callouts are immutable; move to Review before editing.',
        )
      }

      if (requestedStatus === currentStatus) {
        return
      }
    } else if (requestedStatus !== currentStatus) {
      assertAllowed(currentStatus, requestedStatus)
    }

    Object.assign(draft, persistedPatch)
    draft.updatedAt = timestamp
  })
}

/**
 * Mark the parent record as touched by note activity.
 *
 * A locked service is frozen: the backend rejects any write to it that isn't a
 * reopen, so bumping `updatedAt` here would fail the request and drag the note
 * down with it. Its `updatedAt` has nothing left to say once the record is
 * closed anyway — each note carries its own timestamp and audit trail.
 */
function touchService(
  serviceId: string,
  timestamp: string,
  deps: { calloutsRepo: CalloutsRepositoryPort | JobsRepositoryPort },
) {
  const service = deps.calloutsRepo.get(serviceId) as Callout | Job | undefined
  if (isServiceLocked(service?.status)) return

  deps.calloutsRepo.update(serviceId, (draft) => {
    draft.updatedAt = timestamp
  })
}

export function appendLog(
  serviceId: string,
  input: NewLogInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    calloutsRepo: CalloutsRepositoryPort | JobsRepositoryPort
    notesRepo: NotesRepositoryPort
  },
): Log {
  const { log } = appendLogWithTransaction(serviceId, input, deps)
  return log
}

export function appendLogWithTransaction(
  serviceId: string,
  input: NewLogInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    calloutsRepo: CalloutsRepositoryPort | JobsRepositoryPort
    notesRepo: NotesRepositoryPort
  },
): { log: Log; transaction: ReturnType<NotesRepositoryPort['insert']> } {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  const log = {
    id: deps.idGenerator.generateId(),
    serviceId,
    createdAt: timestamp,
    updatedAt: timestamp,
    ...input,
    authorId: currentUser.id,
  } as Log

  const transaction = deps.notesRepo.insert(log)
  touchService(serviceId, timestamp, deps)

  return { log, transaction }
}

export function appendStatusChange(
  serviceId: string,
  input: NewStatusChangeInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    calloutsRepo: CalloutsRepositoryPort | JobsRepositoryPort
    statusChangesRepo: StatusChangesRepositoryPort
  },
): StatusChangeLog {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  if (input.newStatus === 'New') {
    throw new Error('Status changes cannot target New.')
  }

  // A move to the same status is allowed only to update its reason text — it is
  // still recorded as a fresh audit entry, so a reason is required.
  if (input.previousStatus === input.newStatus && !input.reason?.trim()) {
    throw new Error('A same-status change needs a reason to record.')
  }


  const currentService = deps.calloutsRepo.get(serviceId) as Callout | Job | undefined
  const currentStatus = currentService?.status as ServiceStatus | undefined

  if (input.previousStatus === 'New') {
    if (currentStatus && input.newStatus !== currentStatus) {
      throw new Error(
        `Invalid bootstrap status change: expected target ${currentStatus}, got ${input.newStatus}.`,
      )
    }
  } else {
    if (!currentStatus) {
      throw new Error('Callout not found.')
    }

    if (input.previousStatus !== currentStatus) {
      throw new Error(
        `Status change stale write: expected previous status ${currentStatus}, got ${input.previousStatus}.`,
      )
    }
  }

  // Review is the callout/session way out of a locked record, so it needs a
  // reason for the same reason leaving any lock does. The rest come from the
  // shared domain rule, which is also what the status dialog reads — so the
  // form and the command can't drift apart on which moves need explaining.
  if (
    (statusRequiresReason(input.newStatus) || input.newStatus === 'Review') &&
    (!input.reason || input.reason.trim().length === 0)
  ) {
    throw new Error(`Reason is required when moving a service to ${input.newStatus}`)
  }

  // An event can only have happened in the past: back-dating is the point,
  // future-dating would be a typo (or a way to game the audit trail). An
  // occurredAt equal to now is just dropped — the recorded time already says it.
  // Statuses that don't mark a real-world moment carry no event time at all.
  let occurredAt = statusRecordsAnEventTime(input.newStatus)
    ? input.occurredAt?.trim() || undefined
    : undefined
  if (occurredAt) {
    const occurredMs = new Date(occurredAt).getTime()
    if (Number.isNaN(occurredMs)) {
      throw new Error('Event time is not a valid date.')
    }
    if (occurredMs > new Date(timestamp).getTime()) {
      throw new Error('Event time cannot be in the future.')
    }
    if (occurredMs === new Date(timestamp).getTime()) {
      occurredAt = undefined
    }
  }

  const statusChange: StatusChangeLog = {
    id: deps.idGenerator.generateId(),
    serviceId,
    authorId: currentUser.id,
    createdAt: timestamp,
    ...input,
    occurredAt,
    reason: input.reason?.trim() || undefined,
  }

  deps.statusChangesRepo.insert(statusChange)

  return statusChange
}

/**
 * Edit a note, keeping what it said before.
 *
 * Notes stay editable even once the service is locked — the guarantee is not
 * that they never change, but that every version survives. The superseded
 * text/caption/photo is pushed onto `revisions` before the new value lands.
 */
export function updateLog(
  serviceId: string,
  logId: string,
  patch: UpdateLogInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    calloutsRepo: CalloutsRepositoryPort | JobsRepositoryPort
    notesRepo: NotesRepositoryPort
  },
) {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  deps.notesRepo.update(logId, (draft) => {
    if (draft.serviceId !== serviceId) return

    const textChanged =
      patch.text !== undefined && patch.text !== draft.text
    const captionChanged =
      patch.caption !== undefined && patch.caption !== draft.caption
    // A new upload always supersedes whatever the note was showing. The photo
    // is the one part of a note that cannot be reconstructed from memory, so
    // replacing it is the change most worth keeping a version of.
    const photoChanged =
      Boolean(patch.photoFile || patch.photoFiles?.length) &&
      Boolean(draft.photo || draft.photos?.length)

    if (textChanged || captionChanged || photoChanged) {
      draft.revisions = [
        ...(draft.revisions ?? []),
        {
          text: draft.text,
          caption: draft.caption,
          photo: draft.photo,
          // The whole group this version showed — revisions are recorded on
          // the group, never per photo.
          photos: (draft.photos?.length ?? 0) > 1 ? draft.photos : undefined,
          at: timestamp,
          authorId: currentUser.id,
        },
      ]
    }

    Object.assign(draft, patch)
    if (photoChanged) {
      // The replacement photo's stored filename isn't known until the server
      // names it; clearing the group list lets the read-side derivation take
      // over rather than pinning a stale set.
      draft.photos = undefined
      draft.photoUrls = patch.photoUrl ? [patch.photoUrl] : undefined
    }
    draft.updatedAt = timestamp
  })
  touchService(serviceId, timestamp, deps)
}

/**
 * Soft-delete a note: it stops counting as part of the record but its content
 * is retained and rendered as a tombstone, so deleting can never quietly erase
 * something a locked service relied on.
 */
export function deleteLog(
  serviceId: string,
  logId: string,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    calloutsRepo: CalloutsRepositoryPort | JobsRepositoryPort
    notesRepo: NotesRepositoryPort
  },
) {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  deps.notesRepo.update(logId, (draft) => {
    if (draft.serviceId !== serviceId) return
    if (draft.deletedAt) return
    draft.deletedAt = timestamp
    draft.deletedBy = currentUser.id
    draft.updatedAt = timestamp
  })
  touchService(serviceId, timestamp, deps)
}

/**
 * Hard-delete a note.
 *
 * Reserved for rolling back a note whose photo upload failed — it never became
 * part of the record, so it should leave no tombstone. Everything a user
 * deletes goes through {@link deleteLog} instead.
 */
export function discardLog(
  serviceId: string,
  logId: string,
  deps: {
    clock: ClockPort
    calloutsRepo: CalloutsRepositoryPort | JobsRepositoryPort
    notesRepo: NotesRepositoryPort
  },
) {
  const timestamp = deps.clock.now()
  deps.notesRepo.delete(logId)
  touchService(serviceId, timestamp, deps)
}

/** Notes still part of the record — everything not soft-deleted. */
export function activeLogs<T extends { deletedAt?: string }>(logs: T[]): T[] {
  return (logs ?? []).filter((log) => !log.deletedAt)
}
